import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomerServicePackageStatusChip from './CustomerServicePackageStatusChip';
import { getCustomerServicePackageDisplay } from './servicePackageDisplay';
import { BRAND } from '../../theme/brandTokens';

const DetailRow = ({ label, value, valueSx }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word', ...valueSx }}>
      {value}
    </Typography>
  </Box>
);

const CustomerServicePackageMobileCard = ({
  customerPackage,
  canCancel,
  cancelling,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const display = getCustomerServicePackageDisplay(customerPackage, i18n.language, t);

  return (
    <Box
      id={`customer-service-package-card-${customerPackage.id}`}
      sx={{
        border: `1px solid ${BRAND.line}`,
        borderRadius: 2,
        p: 2,
        bgcolor: BRAND.surface,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: BRAND.text, lineHeight: 1.3, wordBreak: 'break-word' }}
          >
            {display.customerName}
          </Typography>
          {customerPackage.customerEmail ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
              {customerPackage.customerEmail}
            </Typography>
          ) : null}
        </Box>
        <CustomerServicePackageStatusChip effectiveStatus={customerPackage.effectiveStatus} sx={{ flexShrink: 0 }} />
      </Box>

      <Stack spacing={1}>
        <DetailRow
          label={t('servicePackagesManagement.customerPackages.table.package')}
          value={display.packageName}
          valueSx={{ fontWeight: 600 }}
        />
        <DetailRow label={t('servicePackagesManagement.customerPackages.table.uses')} value={display.usesLabel} />
        <DetailRow label={t('servicePackagesManagement.customerPackages.table.channel')} value={display.channelLabel} />
        <DetailRow label={t('servicePackagesManagement.customerPackages.table.purchased')} value={display.purchasedLabel} />
        <DetailRow label={t('servicePackagesManagement.customerPackages.table.expires')} value={display.expiresLabel} />
        <DetailRow
          label={t('servicePackagesManagement.customerPackages.table.price')}
          value={display.priceLabel}
          valueSx={{ fontWeight: 700, color: BRAND.accentText }}
        />
      </Stack>

      {canCancel ? (
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${BRAND.lineSoft}` }}>
          <Button
            id={`customer-service-package-card-cancel-${customerPackage.id}`}
            fullWidth
            size="small"
            variant="outlined"
            color="error"
            disabled={cancelling}
            onClick={() => onCancel(customerPackage)}
          >
            {t('servicePackagesManagement.customer.cancel.action')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default CustomerServicePackageMobileCard;
